import type { ReactNode } from 'react';

import {
  CtdDossierContext,
  Field,
  GatingSubsection,
  NumberedSection,
  ProseSubsection,
  RegaraNote,
  SubsectionHeading,
  obj,
} from './DossierPrimitives';
import { Module2, Module3, Module4, Module5, letterWithoutCitations } from './EctdRenderer';
import { euLegalBasisLabel } from '@/features/dossier/ctdTitle';
import { CtdIdentityCard } from './CtdIdentityCard';

/**
 * EU drug & biologic (CTD) results page — the EU Module 1 + the ICH-harmonised
 * Modules 2–5 shared with the US view. Mirrors the EU CTD PDF
 * (`backend/services/dossier/ectd_renderer/eu.py`): same Module 1 sections, labels
 * and hints. Before this view the page showed EU dossiers in the US layout — FDA
 * Module 1 fields and 21 CFR citations (memo M-RA-261009-BA 001).
 */

const AUTHORITY = 'EU';

// Same note as the PDF (`_REFORM_NOTE` in ectd_renderer/eu.py).
const REFORM_NOTE =
  'Legal framework: Directive 2001/83/EC + Regulation (EC) 726/2004 (valid through the ' +
  '2026-2028 transition to the new EU pharmaceutical legislation). Confirm the currently-' +
  'applicable provisions for this application.';

type Prose = { body?: unknown; citations?: unknown[] } | null;

export function EuCtdRenderer({
  dossier,
  legalBasis,
  productName,
  productType,
  company,
  headingRow,
}: {
  dossier: Record<string, unknown>;
  /** The run's EU pathway (its submission_type, e.g. "art-8-3"). */
  legalBasis: string | null;
  /** The run's product name and type — the cover states both. */
  productName?: string | null;
  productType?: string | null;
  /** The run's company — the cover's fallback when the eAF has no stamped applicant. */
  company?: string | null;
  /** The dossier title and Download PDF, as one row. Rendered BELOW the identity block
   *  and outside Module 1's card — where the device page puts its "Part 1 — …" row. */
  headingRow?: ReactNode;
}) {
  const m1 = obj(dossier, 'module_1_administrative');
  // As in the eCTD PDF: letters keep their line breaks, AI tables are tables, no device wording.
  return (
    <CtdDossierContext.Provider value>
      <div className="space-y-4">
        {/* The cover's identity block - what a reviewer establishes first. LOCKSTEP with the
            PDF's cover_identity_rows (backend/services/dossier/ectd_renderer/eu.py). */}
        <CtdIdentityCard
          applicationForm={obj(m1, 'application_form')}
          // The deterministic label, as the PDF cover uses. The model's own
          // `legal_basis_declaration` is prose for §1.5.x and belongs there — on an identity
          // row it put a multi-sentence declaration on the page where the PDF shows
          // "Article 8(3) — full / complete application".
          legalBasisLabel={euLegalBasisLabel(legalBasis)}
          productName={productName}
          productType={productType}
          company={company}
        />
        {headingRow}
        <EuModule1 m1={m1} legalBasis={legalBasis} />
        <Module2 m2={obj(dossier, 'module_2_summaries')} />
        <Module3
          m3={obj(dossier, 'module_3_quality')}
          regionalHint="EU-specific quality information"
        />
        <Module4 m4={obj(dossier, 'module_4_nonclinical')} authority={AUTHORITY} />
        <Module5 m5={obj(dossier, 'module_5_clinical')} authority={AUTHORITY} />
      </div>
    </CtdDossierContext.Provider>
  );
}

// ── Module 1 — EU Regional Administrative Information ───────────────────

function EuModule1({
  m1,
  legalBasis,
}: {
  m1: Record<string, unknown>;
  legalBasis: string | null;
}) {
  const af = obj(m1, 'application_form');
  const pi = obj(m1, 'product_information');
  // LOCKSTEP with ctdIdentity.ts and with procedure.product_number_hint: the EMA product
  // number is a centralised-procedure identifier and no other procedure has one.
  const isCentralised =
    typeof af.procedure === 'string' && af.procedure.startsWith('Centralised procedure');
  const designations = Array.isArray(af.designations)
    ? af.designations.filter((d): d is string => typeof d === 'string' && d.trim() !== '')
    : [];
  // The agent writes the declaration as a string or as {body} (the PDF accepts both); the
  // pathway's label stands in when it is missing.
  const raw = m1.legal_basis_declaration;
  const written = typeof raw === 'string' ? raw : obj(m1, 'legal_basis_declaration').body;
  const declaration =
    typeof written === 'string' && written.trim() ? written : euLegalBasisLabel(legalBasis);
  return (
    <NumberedSection prefix="Module" num={1} title="EU Regional Administrative Information">
      <ProseSubsection
        number="1.0"
        title="Cover Letter"
        prose={letterWithoutCitations(m1.cover_letter)}
        placeholderHint="Cover letter to the EMA / national competent authority"
      />
      <ProseSubsection
        number="1.5.x"
        title="Legal basis of the application"
        prose={{ body: declaration }}
        placeholderHint="Article of Directive 2001/83/EC relied upon + reference product (if any)"
      />

      <SubsectionHeading number="1.2" title="Application Form (eAF)" />
      <Field
        label="(Invented) name of the medicinal product"
        value={af.product_name}
        fallbackHint="Proposed (invented) name"
      />
      <Field
        label="Active substance(s) / INN"
        value={af.active_substance}
        fallbackHint="Active substance(s) / INN"
      />
      <Field
        label="Pharmaceutical form and strength"
        value={af.pharmaceutical_form_strength}
        fallbackHint="Pharmaceutical form and strength"
      />
      <Field label="ATC code" value={af.atc_code} fallbackHint="ATC code" />
      <Field
        label="Applicant (marketing authorisation holder)"
        value={af.applicant}
        fallbackHint="Applicant / MAH name and address"
      />
      {/* Applicant check (eu_medicinal.checks.applicant._set_applicant_note); lockstep with the PDF. */}
      <RegaraNote
        body={af.applicant_note}
        caption="Applicant check (review before submission)"
        authority="EU"
      />
      {/* Manufacturers check (eu_medicinal.checks.sites.apply_site_check); lockstep with the PDF. */}
      <RegaraNote
        body={af.site_note}
        caption="Manufacturers check (review before submission)"
        authority="EU"
      />
      {/* Standards check (eu_medicinal.checks.standards.apply_standards_check); lockstep with the PDF. */}
      <RegaraNote
        body={af.standards_note}
        caption="Standards check (review before submission)"
        authority="EU"
      />
      <Field
        label="Qualified Person for Pharmacovigilance (QPPV)"
        value={af.qppv}
        fallbackHint="QPPV name + contact (Article 104, Directive 2001/83/EC)"
      />
      <Field
        label="Procedure"
        value={af.procedure}
        fallbackHint="Centralised / Decentralised / Mutual Recognition / National"
      />
      {/* The product number persists for the life of the MA; the procedure number names
          this application (RA, 2026-10-26). CENTRALISED ONLY, in lockstep with the PDF's
          _eaf_row and with cover_identity_rows: a decentralised or national application
          never gets an EMEA/H/C number, and an unset Field renders a hint asking the
          submitter to supply one — the 2026-10-13 staging bug. */}
      {isCentralised && (
        <Field
          label="EMA product number"
          value={af.product_number}
          fallbackHint="EMA product number (EMEA/H/C/xxxxxx)"
        />
      )}
      {/* Centralised-scope check (eu_medicinal.checks.procedure.apply_centralised_scope_note);
          lockstep with the PDF (EAF_ROWS in ectd_renderer/eu.py). Absent on every other run. */}
      <RegaraNote
        body={af.procedure_note}
        caption="Procedure check (review before submission)"
        authority="EU"
      />
      {/* EU HTA JCA scope (eu_medicinal.checks.hta_jca.apply_hta_jca_note). A parallel
          submission to the HTA Coordination Group — never part of the CTD. */}
      <RegaraNote
        body={af.hta_jca_note}
        caption="EU HTA JCA scope (review before submission)"
        authority="EU"
      />
      {/* Designations verified against the run's inputs (eu_medicinal.checks.designations); absent
          when none — lockstep with the PDF's _designations_field. */}
      {designations.length > 0 && (
        <Field label="Designations and opinions" value={designations.join('; ')} />
      )}
      {/* Everything the orphan designation needs a second look for, worst first: a number
          the EMA register holds against a different medicine, one it does not hold, one not
          in force, a lookup that could not settle it, one that never ran, and a reference
          too malformed to look up at all (eu_medicinal.checks.hta_jca). The 2028 JCA wave
          turns on a real EU/3 designation, so an unconfirmed one is never assumed. */}
      <RegaraNote
        body={af.orphan_status_note}
        caption="Orphan status (review before submission)"
        authority="EU"
      />

      <ProseSubsection
        number="1.3"
        title="Product Information (SmPC, labelling, package leaflet)"
        prose={pi.smpc_labelling_pil as Prose}
        placeholderHint="Verbatim SmPC, labelling and package leaflet (Annex I/II/III)"
      />
      <ProseSubsection
        number="1.4"
        title="Information about the Experts"
        prose={m1.expert_declarations as Prose}
        placeholderHint="Quality / nonclinical / clinical expert declarations and CVs"
      />
      <ProseSubsection
        number="1.6"
        title="Environmental Risk Assessment (ERA)"
        prose={null}
        placeholderHint="ERA per EMEA/CHMP/SWP/4447/00 Rev. 1 — Phase I screening and, if triggered, Phase II for a chemical substance; for proteins and other natural substances, a justification for not submitting ERA studies"
      />
      <ProseSubsection
        number="1.8"
        title="Pharmacovigilance (RMP / PSMF)"
        prose={m1.pharmacovigilance as Prose}
        placeholderHint="Risk Management Plan + Pharmacovigilance System Master File reference"
      />
      <GatingSubsection
        number="1.10"
        title="Paediatrics (PIP)"
        gating={m1.paediatric as { gating_response?: unknown; rationale?: unknown } | undefined}
        question="Is a Paediatric Investigation Plan (PIP) / waiver applicable to this application?"
        authority={AUTHORITY}
      />
      <p className="mt-4 mb-0 text-[12.5px] italic text-muted">{REFORM_NOTE}</p>
    </NumberedSection>
  );
}
